import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { buildTransmissionPayload } from '../utils/workflowPlayload'
import { toPJAnswerValue } from 'apps/ids/src/app/components/utils/file'
import { clearPersistedProgress } from '../utils/workflowStorage'
import { useAppDispatch } from '../../../../../store'
import { displaySnackbar } from '../../../../../actions/main'
import { TransmissionStatus } from '../../_props'
import {
    PROGRESS_TICK_INTERVAL_MS,
    MIN_TRANSMISSION_DURATION_MS,
    PROGRESS_CAP_BEFORE_COMPLETE,
    NAVIGATE_AFTER_SUCCESS_DELAY_MS,
} from '../constants'
import { usePostIdsHealthcareservicesHcsIdTransmissionsTransmissionIdSubmit } from '@toktokdoctech/tokapi-client'
import axios from 'axios'

type TransmitProgressionFn = (args: { hcsId: string; transmissionId: string; data: any }) => Promise<any>

/**
 * Handles the final submission of the completed tele-expertise
 * (triggered by the last "Next" button in the workflow) along with
 * its associated UI state (progress bar, sending/success/error status).
 *
 * The progress bar is partially simulated: it automatically progresses
 * up to 85% while the request is in progress (to provide smooth visual
 * feedback), then jumps to 100% once the actual API response is received.
 * A minimum delay (`MIN_TRANSMISSION_DURATION_MS`) ensures the animation
 * has enough time to play even if the API responds very quickly.
 */
export function useTransmissionSender(
    hcsId: string,
    transmissionId: string | undefined,
    transmitProgression: TransmitProgressionFn,
    rawByIdMap: Map<string, any>,
    workflowQuestions: { id: string; inputType?: string }[],
) {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const [status, setStatus] = useState<TransmissionStatus>('idle')
    const [progress, setProgress] = useState(0)
    const retryCountRef = useRef(0)

    const { mutateAsync } = usePostIdsHealthcareservicesHcsIdTransmissionsTransmissionIdSubmit()

    const sendTransmission = async (committedAnswersByQuestionId: Map<string, any[]>) => {
        setStatus('sending')
        setProgress(0)
        retryCountRef.current += 1

        const interval = setInterval(() => {
            setProgress((prev) => {
                if (prev >= PROGRESS_CAP_BEFORE_COMPLETE) return prev
                const increment = 2 + Math.random() * 4
                return Math.min(prev + increment, PROGRESS_CAP_BEFORE_COMPLETE)
            })
        }, PROGRESS_TICK_INTERVAL_MS)

        const minDelay = new Promise((resolve) => setTimeout(resolve, MIN_TRANSMISSION_DURATION_MS))

        try {
            if (!transmissionId) {
                throw new Error('Missing transmission id')
            }

            const progressPayload = buildTransmissionPayload(committedAnswersByQuestionId, null)
            const request = Array.from(committedAnswersByQuestionId.entries()).flatMap(([questionId, answers]) => {
                const questionNode = rawByIdMap.get(questionId)
                const answerId = questionId.startsWith('W.Q.') ? questionId.replace('W.Q.', 'W.A.') : questionId

                if (!answers || answers.length === 0) {
                    return [...(questionNode ? [questionNode] : []), { id: answerId, value: '' }]
                }

                const isPJAnswer = workflowQuestions.some((q) => q.id === questionId && q.inputType === 'pj')
                const values = answers.map(toPJAnswerValue)
                const formattedValue = values.length === 1 && !isPJAnswer ? values[0] : values
                return [
                    ...(questionNode ? [questionNode] : []),
                    {
                        id: answerId,
                        value: formattedValue,
                        ...(isPJAnswer ? { type: 'PJ' } : {}),
                    },
                ]
            })

            const finalPayload = {
                request,
            }

            await Promise.all([
                transmitProgression({
                    hcsId,
                    transmissionId,
                    data: progressPayload,
                }),
                minDelay,
            ])

            await mutateAsync({
                hcsId,
                transmissionId,
                data: finalPayload,
            })

            clearInterval(interval)
            setProgress(100)
            setStatus('success')

            if (transmissionId) {
                clearPersistedProgress(transmissionId)
            }

            dispatch(
                displaySnackbar(
                    'success',
                    'La téléexpertise a bien été transmise au requis !',
                    undefined,
                    '[useTransmissionSender] transmission réussie',
                ),
            )

            setTimeout(() => navigate('/teleexpertises'), NAVIGATE_AFTER_SUCCESS_DELAY_MS)
        } catch (error) {
            clearInterval(interval)
            setProgress(100)

            if (axios.isAxiosError(error)) {
                if (error.response?.data.message.includes('has no secured email')) {
                    setStatus('warning')
                    return
                }
            }

            setStatus('error')
            dispatch(
                displaySnackbar(
                    'error',
                    'Erreur lors de la transmission',
                    undefined,
                    '[useTransmissionSender] transmission échouée',
                ),
            )
        }
    }

    return { status, progress, sendTransmission }
}
